import {useId, useState} from 'react'
import {useFetcher} from 'react-router'
import {DEFAULT_SIGN_IN_CODE_COPY, type SignInCodeCopy} from '@core/auth-ui/signInCopy'
import styles from './SignInCodeForm.module.scss'

type CodeAnswer = {error?: 'wrong' | 'dead'}

/**
 * The six digits from the sign-in mail, typed in the browser that asked for it
 * (`magicLinkCodeAction`). It posts with a fetcher: a wrong or dead code comes back as data and the
 * waiting view stays where it is; a right one comes back as the redirect, which the router follows.
 * Spaces are welcome ("123 456").
 */
export function SignInCodeForm({copy = DEFAULT_SIGN_IN_CODE_COPY}: {copy?: SignInCodeCopy}) {
    const fetcher = useFetcher<CodeAnswer>()
    const inputId = useId()
    const [typed, setTyped] = useState('')
    const submitting = fetcher.state !== 'idle'
    const error = fetcher.data?.error
    const errorText = error === 'wrong' ? copy.wrong : error === 'dead' ? copy.dead : null

    return (
        <fetcher.Form method="POST" action="/auth/magic-link/code" className={styles.form}>
            <label htmlFor={inputId} className={styles.label}>
                {copy.label}
            </label>
            <div className={styles.row}>
                <input
                    id={inputId}
                    name="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9 ]*"
                    maxLength={10}
                    placeholder="123 456"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    className={styles.input}
                    aria-invalid={errorText ? true : undefined}
                    aria-describedby={errorText ? `${inputId}-error` : undefined}
                />
                <button type="submit" className={styles.button} disabled={submitting || typed.replace(/\D/g, '').length < 6}>
                    {submitting ? copy.submitting : copy.submit}
                </button>
            </div>
            {errorText && (
                <p id={`${inputId}-error`} className={styles.error} role="alert">
                    {errorText}
                </p>
            )}
        </fetcher.Form>
    )
}
